import { useState, useCallback } from 'react'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import { UploadDocumentType } from '@/constants/businessConstants'
import { PrefixedUserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import type { ContractorDocumentsStepContext } from '@/features/bookkeeping/end-of-year-review/types'
import type { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { useDocuments } from '@/features/bookkeeping/hooks/use-documents'
import { useNotRelevantDocuments } from '@/features/bookkeeping/hooks/use-not-relevant-documents'
import { useDocumentReuploadStatus } from '../../../hooks'
import { NotRelevantModal } from '@/features/bookkeeping/end-of-year-review/not-relevant-modal'
import { getInstructionsLink } from '@/features/bookkeeping/end-of-year-review/documents-map'
import { handleDocumentDeletion } from '../document-deletion.helpers'
import './styles.scss'

export interface ContractorDocumentProps {
  stepContext: ContractorDocumentsStepContext | undefined
  setStepContext: (context: ContractorDocumentsStepContext) => void
  provider: string
  userDocuments: UserDocument[]
  isFollowUp: boolean
  taxYear: number
  /** When true, renders section header. When false, renders only the document card. */
  showHeader?: boolean
  stepStatus?: string
}

/**
 * ContractorDocument
 *
 * Reusable component for managing contractor document uploads for a specific provider.
 * This is a provider-based document type that handles contractor payment documents.
 *
 * Documents handled:
 * - Contractor payment reports (NOT Form 1099-NEC per requirements)
 *
 * Features:
 * - Provider-based filtering (Gusto, Heard, etc.)
 * - File upload with V1 modal
 * - "Not relevant" workflow
 * - Embedded dialog management
 *
 * @example
 * ```tsx
 * <ContractorDocument
 *   stepContext={contractorStepContext}
 *   setStepContext={setContractorStepContext}
 *   provider="Gusto"
 *   userDocuments={documents}
 *   isFollowUp={false}
 *   taxYear={2024}
 * />
 * ```
 */
export function ContractorDocument({
  stepContext,
  setStepContext,
  provider,
  userDocuments,
  isFollowUp,
  taxYear,
  showHeader = true,
  stepStatus,
}: ContractorDocumentProps) {
  const [uploadDialog, setUploadDialog] = useState(false)
  const [uploadMetadata, setUploadMetadata] = useState<{
    categoryCode: string
    provider: string
  } | null>(null)

  const contractorPaymentsReport =
    PrefixedUserDocumentCategoryIdentifier.contractorPaymentsReport(
      String(taxYear)
    )

  // Use documents hook for filtering and status
  const { filterDocuments, getStatus } = useDocuments({
    stepContextDocuments: stepContext?.documents,
    userDocuments,
  })

  // Use not relevant documents hook
  const { isMarkedNotRelevant, handleNotRelevant, notRelevantModal } =
    useNotRelevantDocuments(stepContext, setStepContext)

  // Get re-upload status for contractor payment report
  const contractorReportReuploadStatus = useDocumentReuploadStatus(
    stepContext,
    contractorPaymentsReport,
    provider,
    isFollowUp
  )

  const handleUploadClick = useCallback(
    (categoryCode?: string) => {
      if (categoryCode) {
        setUploadMetadata({ categoryCode, provider })
      } else {
        setUploadMetadata(null)
      }
      setUploadDialog(true)
    },
    [provider]
  )

  const handleFileUploaded = useCallback(
    (uploadedFile: UploadedFile) => {
      if (!uploadMetadata) {
        // Handle "Other documents" upload
        const otherDocumentIds = [
          ...(stepContext?.otherDocumentIds ?? []),
          uploadedFile.id,
        ]
        setStepContext({ ...(stepContext ?? {}), otherDocumentIds })
        setUploadDialog(false)
        return
      }

      const { categoryCode, provider } = uploadMetadata

      // Find the original document that needs re-upload (if it exists)
      const originalDocument = stepContext?.documents?.find(
        (doc) => doc.categoryCode === categoryCode && doc.provider === provider
      )

      // Replace or add the document to the list
      // Preserve requestReUpload and adminNote from original, set updatedByUser: true
      const documents = [
        ...(stepContext?.documents?.filter(
          (doc) =>
            !(doc.categoryCode === categoryCode && doc.provider === provider)
        ) ?? []),
        {
          documentId: uploadedFile.id,
          categoryCode,
          provider,
          adminNote: originalDocument?.adminNote,
          requestReUpload:
            originalDocument?.requestReUpload ??
            (isFollowUp ? true : undefined),
          updatedByUser: true,
        },
      ]

      setStepContext({ ...(stepContext ?? {}), documents })
      setUploadDialog(false)
      setUploadMetadata(null)
    },
    [uploadMetadata, stepContext, setStepContext, isFollowUp]
  )

  // Handle document deletion - preserve requestReUpload state in follow-up mode
  const handleDocumentDeleted = useCallback(
    (deletedDocumentId: number) => {
      const documents = handleDocumentDeletion(
        stepContext?.documents,
        (doc) => doc.documentId === deletedDocumentId,
        isFollowUp,
        { preserveProvider: true }
      )
      setStepContext({ ...(stepContext ?? {}), documents })
    },
    [stepContext, setStepContext, isFollowUp]
  )

  // For followup: Check if step requires user action - only render section if status is user_action_required
  if (isFollowUp && stepStatus !== 'user_action_required') {
    return null
  }

  return (
    <>
      <div className="contractor-document">
        {showHeader && <h2>Contractor Payments</h2>}

        <div className="contractor-document__card-container">
          {/* Contractor Payment Report - only document required per requirements */}
          <DocumentUploadCard
            name={`${provider} - Contractor Payment Report`}
            description="This totals all payments made to 1099 independent contractors."
            href={getInstructionsLink(
              contractorPaymentsReport,
              provider,
              taxYear.toString()
            )}
            status={getStatus(
              contractorPaymentsReport,
              provider,
              isMarkedNotRelevant
            )}
            documents={filterDocuments(contractorPaymentsReport, provider)}
            handleUploadBtn={() => handleUploadClick(contractorPaymentsReport)}
            notRelevant={isMarkedNotRelevant(
              contractorPaymentsReport,
              provider
            )}
            setChecked={() =>
              handleNotRelevant(contractorPaymentsReport, provider)
            }
            isFollowUp={isFollowUp}
            isRequiredReviewed={contractorReportReuploadStatus}
            onDocumentDeleted={handleDocumentDeleted}
          />
        </div>
      </div>

      {/* Embedded V1 FileUploadModal */}
      <FileUploadModal
        open={uploadDialog}
        close={() => setUploadDialog(false)}
        documentType={UploadDocumentType.BOOKKEEPING}
        categoryInternalName={uploadMetadata?.categoryCode}
        userFacing
        year={taxYear.toString()}
        setUploadedFile={handleFileUploaded}
        isUploadOnly
      />

      {/* Not relevant confirmation modal */}
      <NotRelevantModal
        isOpen={notRelevantModal.isOpen}
        handleClose={notRelevantModal.handleClose}
        setConfirmNotRelevant={notRelevantModal.setConfirmNotRelevant}
      />
    </>
  )
}
